import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, MapPin, Plus, Trash2, Save } from "lucide-react";
import { API } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * Edit a booking's pickup / drop-off / stops after it's been created.
 *
 * Works for two audiences:
 *   1. Customers on their own booking (booking.user_id === current user)
 *   2. Admins on any booking
 *
 * Backend: PATCH /api/bookings/{id} — enforces ownership/admin, allows edits
 * up to (but not including) completion. Sends the driver a push when a
 * live trip's route changes.
 *
 * Reuses AddressAutocomplete so the picker matches the booking flow.
 */
export default function EditRouteDialog({ open, booking, onClose, onSaved }) {
  const [pickup, setPickup] = useState({ address: "", coords: null });
  const [dropoff, setDropoff] = useState({ address: "", coords: null });
  const [stops, setStops] = useState([]);
  const [saving, setSaving] = useState(false);

  // Seed from booking whenever it opens
  useEffect(() => {
    if (!open || !booking) return;
    setPickup({
      address: booking.pickup_address || "",
      coords: booking.pickup_coords || null,
    });
    setDropoff({
      address: booking.dropoff_address || "",
      coords: booking.dropoff_coords || null,
    });
    setStops(
      (booking.stops || []).map((s) => ({
        address: s.address || "",
        coords: s.coords || null,
      }))
    );
  }, [open, booking]);

  if (!open || !booking) return null;

  const addStop = () => {
    if (stops.length >= 5) {
      toast.error("Up to 5 stops.");
      return;
    }
    setStops((prev) => [...prev, { address: "", coords: null }]);
  };
  const removeStop = (idx) => {
    setStops((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateStop = (idx, val) => {
    setStops((prev) => prev.map((s, i) => (i === idx ? val : s)));
  };

  const save = async () => {
    // Fall back to the booking's original coords if the user didn't retype —
    // AddressAutocomplete clears coords the moment the input changes, but if
    // the value still matches what we opened with, the original coords are
    // still valid. This avoids "pick a valid address" errors when the user
    // only wanted to change ONE field (e.g. add a stop) and never touched
    // pickup/drop-off.
    const effectivePickup = {
      address: pickup.address,
      coords: pickup.coords
        || (pickup.address === booking.pickup_address ? booking.pickup_coords : null),
    };
    const effectiveDropoff = {
      address: dropoff.address,
      coords: dropoff.coords
        || (dropoff.address === booking.dropoff_address ? booking.dropoff_coords : null),
    };
    const originalStops = Array.isArray(booking.stops) ? booking.stops : [];
    const effectiveStops = stops.map((s, idx) => {
      if (s.coords) return s;
      const orig = originalStops[idx];
      if (orig && orig.address === s.address) return { address: s.address, coords: orig.coords };
      return s;
    });

    if (!effectivePickup.address || !effectivePickup.coords) {
      toast.error("Pick a valid pickup address from the dropdown.");
      return;
    }
    if (!effectiveDropoff.address || !effectiveDropoff.coords) {
      toast.error("Pick a valid drop-off address from the dropdown.");
      return;
    }
    for (const [i, s] of effectiveStops.entries()) {
      if (s.address && !s.coords) {
        toast.error(`Stop ${i + 1}: pick an address from the dropdown.`);
        return;
      }
    }
    const payload = {
      pickup_address: effectivePickup.address,
      pickup_coords: effectivePickup.coords,
      dropoff_address: effectiveDropoff.address,
      dropoff_coords: effectiveDropoff.coords,
      stops: effectiveStops
        .filter((s) => s.address && s.coords)
        .map((s) => ({ address: s.address, coords: s.coords })),
    };
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/bookings/${booking.id}`, payload);
      const changed = (data.changed_fields || []).length;
      toast.success(
        changed === 0
          ? "No changes — route already up to date."
          : `Route updated. ${booking.driver_id ? "Driver has been notified." : ""}`,
        { duration: 6000 }
      );
      onSaved?.(data.booking);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update route.");
    } finally {
      setSaving(false);
    }
  };

  const midTrip = booking.status === "in_progress";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={() => !saving && onClose?.()}
      data-testid="edit-route-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-[#121212] border border-white/10 rounded-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div>
            <h3 className="text-base font-bold text-white">Edit route</h3>
            <p className="text-xs text-zinc-500">
              {midTrip
                ? "This trip is live — your driver will be notified of the change."
                : "Adjust pickup, drop-off, or add stops."}
            </p>
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            className="p-2 rounded-lg hover:bg-white/5 text-white/50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {midTrip && (
            <div className="text-[11px] rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2">
              You&apos;re editing a trip that&apos;s already in progress. Your driver will get a push the moment you tap Save.
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1 block">
              Pickup
            </label>
            <AddressAutocomplete
              value={pickup.address}
              onChange={(address, coords) => setPickup({ address, coords })}
              accent="emerald"
              placeholder="New pickup address"
              testId="edit-route-pickup"
            />
          </div>

          {stops.map((stop, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
                  Stop {idx + 1}
                </label>
                <button
                  onClick={() => removeStop(idx)}
                  className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1"
                  data-testid={`edit-route-remove-stop-${idx}`}
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
              <AddressAutocomplete
                value={stop.address}
                onChange={(address, coords) => updateStop(idx, { address, coords })}
                accent="amber"
                placeholder={`Stop ${idx + 1} address`}
                testId={`edit-route-stop-${idx}`}
              />
            </div>
          ))}

          {stops.length < 5 && (
            <button
              onClick={addStop}
              className="w-full text-xs text-white/60 hover:text-white/90 border border-dashed border-white/10 hover:border-white/25 rounded-lg py-2 flex items-center justify-center gap-1"
              data-testid="edit-route-add-stop"
            >
              <Plus className="w-3 h-3" /> Add stop
            </button>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#4D88FF] font-bold mb-1 block">
              Drop-off
            </label>
            <AddressAutocomplete
              value={dropoff.address}
              onChange={(address, coords) => setDropoff({ address, coords })}
              accent="blue"
              placeholder="New drop-off address"
              testId="edit-route-dropoff"
            />
          </div>
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-white/5">
          <Button
            onClick={onClose}
            disabled={saving}
            variant="outline"
            className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5"
            data-testid="edit-route-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="flex-1 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold"
            data-testid="edit-route-save"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : "Save route"}
          </Button>
        </div>
      </div>
    </div>
  );
}
